import type React from "react";
import {
  Compass,
  Network,
  BarChart3,
  BookOpen,
  Map,
  Highlighter,
  Sparkles,
  FolderOpen,
  MessageSquare,
  Mail,
  Tag,
  MessageCircle,
  PenSquare,
  Workflow,
  FileUp,
  Filter,
  Scale,
  Flag,
  Download,
  Brain,
  Telescope,
  Sliders,
  Globe2,
  type LucideIcon,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GuideData {
  seedCount: number;
  trawledCount: number;
  scoredCount: number;
  readOrSkimmedCount: number;
  claimCount: number;
  totalPapers: number;
  hasDebateMap: boolean;
  hasClaimsWithEvidence: boolean;
  monitorEnabled: boolean;
  conceptCreatedAt: string;
  digestFrequency: string;
  pdfOpenCount: number;
  hasHighlights: boolean;
  hasUsedSmartTrawl: boolean;
  hasWorkingSets: boolean;
  hasUsedPositionAnalysis: boolean;
  hasUsedDismissSimilar: boolean;
  hasUsedResearchInbox: boolean;
  hasTaggedOrNotedPaper: boolean;
  hasUsedDynamicChat: boolean;
  hasRunCoverageAudit: boolean;
  hasOpenedWritingWorkspace: boolean;
  hasUsedMonitorTriage: boolean;
  hasOverriddenScore: boolean;
  hasRefinedConcept: boolean;
  hasRescoredAfterRefine: boolean;
  hasViewedLandscape: boolean;
}

export type EntryPath = "discover" | "import" | "zotero";

// ---------------------------------------------------------------------------
// Extended types (2026-04-10)
// ---------------------------------------------------------------------------

export interface NextStep {
  label: string;
  isDone?: (d: GuideData) => boolean;
}

interface EntryContent {
  label: string;
  tagline?: string;
  description: string;
}

interface BaseEntry {
  id: string;
  icon: LucideIcon;
  content: EntryContent;
}

export interface CoreStepEntry extends BaseEntry {
  type: "core";
  content: EntryContent & {
    activeDescription?: string;
    activeHints?: NextStep[];
  };
  precondition: (d: GuideData) => boolean;
  isComplete: (d: GuideData) => boolean;
  relatedWalkthrough?: string;
}

export interface FeatureEntry extends BaseEntry {
  type: "feature";
  parentStep: string;
  color: "purple" | "blue" | "peach" | "green";
  content: EntryContent & {
    nextSteps: NextStep[];
    ctaLabel?: string;
    videoUrl?: string | null;
  };
  appearThreshold?: (d: GuideData) => boolean;
  precondition: (d: GuideData) => boolean;
  isComplete: (d: GuideData) => boolean;
  priority?: number;
  relatedWalkthrough?: string;
  targetRef?: string;
}

export type WalkthroughGroup =
  | "start-here"
  | "foundations"
  | "corpus"
  | "analysis"
  | "output";

export interface WalkthroughEntry extends BaseEntry {
  type: "walkthrough";
  group: WalkthroughGroup;
  content: EntryContent & {
    estMinutes: number;
    status: "live" | "soon";
    mdxModule?: () => Promise<{ default: React.ComponentType }>;
    plannedSections?: string[];
  };
  relatedWalkthroughs?: string[];
}

export type GuideEntry = CoreStepEntry | FeatureEntry | WalkthroughEntry;

// ---------------------------------------------------------------------------
// FEATURE CONTENT (top of file — scan like a feature catalog)
// ---------------------------------------------------------------------------

const FEATURE_CONTENT: Record<string, FeatureEntry["content"]> = {
  "highlights": {
    label: "Highlight key passages",
    tagline: "Capture evidence directly from papers",
    description:
      "Highlight important passages in PDFs to capture evidence directly from the papers you read. Highlights feed Spot Trawl and are captured as evidence in the Evidence Map.",
    nextSteps: [
      { label: "Open a high-scoring PDF", isDone: (d) => d.pdfOpenCount > 0 },
      { label: "Select text in the viewer to create a highlight", isDone: (d) => d.hasHighlights },
    ],
    ctaLabel: "Open top-scored PDF",
    videoUrl: null,
  },
  "smart-trawl": {
    label: "Spot Trawl",
    tagline: "Highlight-guided discovery",
    description:
      "Find papers cited near your highlights — a targeted citation search centred on the passages that matter to you, not just any paper in the network.",
    nextSteps: [
      { label: "Open a high-scoring PDF", isDone: (d) => d.pdfOpenCount > 0 },
      { label: "Highlight 2–3 key passages", isDone: (d) => d.hasHighlights },
      { label: "Click Spot Trawl in the toolbar", isDone: (d) => d.hasUsedSmartTrawl },
    ],
    ctaLabel: "Run Spot Trawl",
    videoUrl: null,
  },
  "working-sets": {
    label: "Create working sets",
    tagline: "Slice your corpus into focused subgroups",
    description:
      "Working sets let you organise papers into focused sub-collections — by theme, chapter, or any slice that helps you write and analyse.",
    nextSteps: [
      { label: "Filter papers by score or tag", isDone: (d) => d.totalPapers >= 10 },
      { label: 'Click "Save as working set" in the filter bar', isDone: (d) => d.hasWorkingSets },
    ],
    ctaLabel: "Create a working set",
    videoUrl: null,
  },
  "field-debate": {
    label: "Explore field debate",
    tagline: "See the intellectual camps in your field",
    description:
      "Generate an AI map of the major camps, claims, and debates in your field — based on the papers you've scored. Helps you see where your argument fits.",
    nextSteps: [
      { label: "Score at least 15 papers", isDone: (d) => d.scoredCount >= 15 },
      { label: "Read 2+ highly scored papers", isDone: (d) => d.readOrSkimmedCount >= 2 },
      { label: "Open Field Debate from the Analyze page", isDone: (d) => d.hasDebateMap },
    ],
    ctaLabel: "Open Field Debate",
    videoUrl: null,
  },
  "position-analysis": {
    label: "Analyse your position",
    tagline: "Locate your argument in the field",
    description:
      "Compare your Evidence Map against the Field Debate to see where your claims align, where they push back, and where gaps remain.",
    nextSteps: [
      { label: "Generate a Field Debate map", isDone: (d) => d.hasDebateMap },
      { label: "Add claims with evidence", isDone: (d) => d.hasClaimsWithEvidence },
      { label: "Click Position Analysis", isDone: (d) => d.hasUsedPositionAnalysis },
    ],
    ctaLabel: "Run Position Analysis",
    videoUrl: null,
  },
  "calibrate-scoring": {
    label: "Calibrate scoring",
    tagline: "Teach PaperChase your taste",
    description:
      "Scoring isn't a one-shot AI verdict — every thumbs, dismissal, highlight, and note tunes the next pass. The more you teach it, the sharper it gets.",
    nextSteps: [
      { label: "Open a top-scored paper", isDone: (d) => d.pdfOpenCount > 0 },
      { label: "Thumbs up/down or override the score", isDone: (d) => d.hasOverriddenScore },
      { label: "Dismiss with a reason", isDone: (d) => d.hasUsedDismissSimilar },
    ],
    ctaLabel: "Show me the score controls",
    videoUrl: null,
  },
  "refine-concept": {
    label: "Refine your concept",
    tagline: "Scores look off? Sharpen the lens",
    description:
      "If the top scores look wrong, your concept might be too broad or too narrow. Refining the description and rescoring is the highest-leverage thing you can do.",
    nextSteps: [
      { label: "Review the top 10 scored papers", isDone: (d) => d.scoredCount >= 10 },
      { label: "Open Concept refinement", isDone: (d) => d.hasRefinedConcept },
      { label: "Rescore the corpus", isDone: (d) => d.hasRescoredAfterRefine },
    ],
    ctaLabel: "Refine concept",
    videoUrl: null,
  },
  "dismiss-similar": {
    label: "Dismiss similar",
    tagline: "Cull irrelevant papers in bulk",
    description:
      "When you dismiss a paper, PaperChase can find others like it using embeddings + LLM classification. One click cleans out a whole category of noise.",
    nextSteps: [
      { label: "Dismiss one irrelevant paper" },
      { label: 'Click "Dismiss similar" in the confirmation', isDone: (d) => d.hasUsedDismissSimilar },
    ],
    ctaLabel: "Show me",
    videoUrl: null,
  },
  "tags-notes": {
    label: "Tags & notes",
    tagline: "Curate papers with your own labels",
    description:
      "Add tags and notes to any paper to build your own curation layer. Tags filter the corpus; notes feed relevance scoring for this concept.",
    nextSteps: [
      { label: "Open a paper detail pane", isDone: (d) => d.pdfOpenCount > 0 },
      { label: "Add a tag or a note", isDone: (d) => d.hasTaggedOrNotedPaper },
    ],
    ctaLabel: "Open top-scored PDF",
    videoUrl: null,
  },
  "dynamic-chat": {
    label: "Dynamic chat",
    tagline: "LLM-suggested questions per paper",
    description:
      "Every paper detail pane suggests questions tailored to the paper — ask PaperChase for the method, the contribution, or how it compares to another paper.",
    nextSteps: [
      { label: "Open a paper detail pane", isDone: (d) => d.pdfOpenCount > 0 },
      { label: "Click a suggested question", isDone: (d) => d.hasUsedDynamicChat },
    ],
    ctaLabel: "Open chat",
    videoUrl: null,
  },
  "coverage-audit": {
    label: "Coverage audit",
    tagline: "Find the gaps in your corpus",
    description:
      "Scan your scored corpus for missing viewpoints, underrepresented methods, and citation gaps. Helps you know what you don't know.",
    nextSteps: [
      { label: "Score at least 20 papers", isDone: (d) => d.scoredCount >= 20 },
      { label: "Open Coverage Audit from the Analyze page", isDone: (d) => d.hasRunCoverageAudit },
    ],
    ctaLabel: "Open Coverage Audit",
    videoUrl: null,
  },
  "writing-workspace": {
    label: "Writing workspace",
    tagline: "Draft with your evidence beside you",
    description:
      "A focused editor where your claims and evidence sit beside your draft. Export to Markdown, BibTeX, or DOCX when ready.",
    nextSteps: [
      { label: "Build an evidence map with 3+ claims", isDone: (d) => d.claimCount >= 3 },
      { label: "Open the Writing Workspace", isDone: (d) => d.hasOpenedWritingWorkspace },
    ],
    ctaLabel: "Open Writing Workspace",
    videoUrl: null,
  },
  "monitor-triage": {
    label: "Monitor triage",
    tagline: "4-layer filtering for new papers",
    description:
      "When the Living Literature Monitor delivers a new batch, the triage UI classifies papers into four layers: must-read, maybe, skim, skip. Turn firehose into focus.",
    nextSteps: [
      { label: "Enable the monitor for this concept", isDone: (d) => d.monitorEnabled },
      { label: "Open the Monitor page and triage a batch", isDone: (d) => d.hasUsedMonitorTriage },
    ],
    ctaLabel: "Open Monitor",
    videoUrl: null,
  },
  "monitor-digest": {
    label: "Set up digest",
    tagline: "Email-delivered paper updates",
    description:
      "Configure a weekly or monthly digest so new papers on your topic arrive in your inbox — no need to check the app to stay current.",
    nextSteps: [
      { label: "Enable monitor for this concept", isDone: (d) => d.monitorEnabled },
      { label: "Set digest frequency in Settings", isDone: (d) => !!d.digestFrequency && d.digestFrequency !== "off" },
    ],
    ctaLabel: "Open digest settings",
    videoUrl: null,
  },
  "literature-landscape": {
    label: "Literature Landscape",
    tagline: "See your corpus as intellectual geography",
    description:
      "Project your entire corpus into a 2D map based on semantic similarity. Papers that argue alike cluster together, field debate camps appear as colored regions, and your thesis is marked as a beacon.",
    nextSteps: [
      { label: "Run Field Debate on the Analyze page", isDone: (d) => d.hasDebateMap },
      { label: "Open the Literature Landscape", isDone: (d) => d.hasViewedLandscape },
    ],
    ctaLabel: "View Landscape",
    videoUrl: null,
  },
};

// ---------------------------------------------------------------------------
// WALKTHROUGH CONTENT
// ---------------------------------------------------------------------------

const WALKTHROUGH_CONTENT: Record<string, WalkthroughEntry["content"]> = {
  "lifecycle-of-an-argument": {
    label: "Lifecycle of an Argument",
    tagline: "The 5-minute master overview",
    description: "How a research argument moves from an idea to a finished draft in PaperChase.",
    estMinutes: 5,
    status: "live",
    mdxModule: () => import("@/content/walkthroughs/lifecycle-of-an-argument.mdx"),
  },
  "conceptual-frameworks": {
    label: "Conceptual Frameworks",
    tagline: "Why focused concepts matter",
    description:
      "What makes a concept powerful, why tight framing beats broad framing, and how to rewrite fuzzy concepts into sharp ones.",
    estMinutes: 3,
    status: "live",
    mdxModule: () => import("@/content/walkthroughs/conceptual-frameworks.mdx"),
  },
  "paperchase-learns-from-you": {
    label: "PaperChase learns from you",
    tagline: "Feedback, personalization, calibration",
    description:
      "How every thumbs-up, dismissal, exclusion reason, and annotation tunes scoring to your preferences for this concept.",
    estMinutes: 3,
    status: "live",
    mdxModule: () => import("@/content/walkthroughs/paperchase-learns-from-you.mdx"),
  },
  "discovery-via-citation-networks": {
    label: "Discovery via citation networks",
    tagline: "Seeds, trawl, snowball, Spot Trawl",
    description:
      "How PaperChase explores the citation graph — from seed discovery through Spot Trawl — to find the papers that matter.",
    estMinutes: 4,
    status: "live",
    mdxModule: () => import("@/content/walkthroughs/discovery-via-citation-networks.mdx"),
  },
  "importing-existing-bibliographies": {
    label: "Importing existing bibliographies",
    tagline: "BibTeX, Zotero, PDFs — metadata vs full-text",
    description:
      "How to bring existing collections into PaperChase and understand the difference between bibliographic metadata and actual full-text content.",
    estMinutes: 4,
    status: "live",
    mdxModule: () => import("@/content/walkthroughs/importing-existing-bibliographies.mdx"),
  },
  "corpus-curation": {
    label: "Corpus curation",
    tagline: "Scoring, filtering, dismissing, working sets",
    description:
      "How to clean up a trawled corpus — filter by score, dismiss noise, promote standouts, and organise the rest into working sets.",
    estMinutes: 3,
    status: "live",
    mdxModule: () => import("@/content/walkthroughs/corpus-curation.mdx"),
  },
  "building-your-argument": {
    label: "Building your argument",
    tagline: "Evidence Map, claims, evidence attachment",
    description:
      "The Evidence Map turns your reading into a structured argument: claims, links, and the evidence that supports or challenges them.",
    estMinutes: 4,
    status: "live",
    mdxModule: () => import("@/content/walkthroughs/building-your-argument.mdx"),
  },
  "leveraging-the-analyze-page": {
    label: "Leveraging the Analyze page",
    tagline: "Comparison, coverage audit, field debate",
    description:
      "The Analyze page hosts the heavyweight tools: comparison matrices, coverage audits, blind spot analysis, and field debate mapping.",
    estMinutes: 4,
    status: "live",
    mdxModule: () => import("@/content/walkthroughs/leveraging-the-analyze-page.mdx"),
  },
  "planting-the-flag": {
    label: "Planting the flag",
    tagline: "Evidence Map × Field Debate",
    description:
      "Combining the Evidence Map with the Field Debate to locate your argument, identify alignments and tensions, and plant a defensible flag.",
    estMinutes: 3,
    status: "live",
    mdxModule: () => import("@/content/walkthroughs/planting-the-flag.mdx"),
  },
  "using-the-output": {
    label: "Using the output",
    tagline: "Writing Workspace and exports",
    description:
      "Moving from evidence map to draft: the Writing Workspace, lit review export, BibTeX, DOCX, and outline export.",
    estMinutes: 3,
    status: "live",
    mdxModule: () => import("@/content/walkthroughs/using-the-output.mdx"),
  },
};

export interface GuideStep {
  id: string;
  label: string;
  icon: LucideIcon;
  type: "core" | "milestone";
  parentStep?: string;
  entryPaths?: EntryPath[];
  /** Returns true when the step should be visible/teased in the UI */
  appearThreshold?: (data: GuideData) => boolean;
  /** Returns true when the step is actionable (user can attempt it) */
  precondition: (data: GuideData) => boolean;
  /** Returns true when the step has been completed */
  isComplete: (data: GuideData) => boolean;
  targetRef?: string;
  message: string;
  /** Short benefit line shown below the step label when active in the journey bar */
  activeDescription?: string;
  /** Actionable sub-steps shown in a detail callout when this step is active */
  activeHints?: string[];
  runningMessage?: string;
  priority?: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns the number of days since a concept was created */
function daysSince(isoDate: string): number {
  const created = new Date(isoDate).getTime();
  const now = Date.now();
  return (now - created) / (1000 * 60 * 60 * 24);
}

// ---------------------------------------------------------------------------
// Core Steps (5)
// ---------------------------------------------------------------------------

export const CORE_STEPS: CoreStepEntry[] = [
  {
    id: "seed",
    type: "core",
    icon: Compass,
    content: {
      label: "Add seed papers",
      description:
        "Start by adding papers that are central to your topic. These seeds anchor the citation search.",
      activeDescription:
        "Seeds shape everything — your trawl, scores, and debate map all build from these starting points",
      activeHints: [
        { label: 'Use "Discover" to find seeds from your concept description' },
        { label: 'Or "Import" a bibliography you already have' },
        { label: 'Or "Zotero" to pull from a collection' },
      ],
    },
    precondition: () => true,
    isComplete: (d) => d.seedCount > 0,
    relatedWalkthrough: "conceptual-frameworks",
  },
  {
    id: "trawl",
    type: "core",
    icon: Network,
    content: {
      label: "Trawl citations",
      description: "Run a trawl to discover papers citing and cited by your seeds.",
      activeDescription:
        "Trawling finds papers you'd miss searching manually — following citation chains surfaces the real conversation",
      activeHints: [
        { label: "Click Trawl in the toolbar above the paper table" },
        { label: "Wait for the trawl to finish (progress bar at the bottom)" },
      ],
    },
    precondition: (d) => d.seedCount > 0,
    isComplete: (d) => d.trawledCount > 0,
    relatedWalkthrough: "discovery-via-citation-networks",
  },
  {
    id: "score",
    type: "core",
    icon: BarChart3,
    content: {
      label: "Score papers",
      description:
        "Score trawled papers for relevance to your concept so the most important ones surface first.",
      activeDescription:
        "AI scoring ranks every paper against your specific concept — not just keywords, but actual relevance to your question",
      activeHints: [
        { label: "Click Score in the toolbar to score trawled papers" },
        { label: "Scores appear in the table sorted highest-first" },
      ],
    },
    precondition: (d) => d.trawledCount > 0,
    isComplete: (d) => d.scoredCount > 0,
    relatedWalkthrough: "corpus-curation",
  },
  {
    id: "review",
    type: "core",
    icon: BookOpen,
    content: {
      label: "Review papers",
      description:
        "Review your highest-scoring papers to build understanding and capture evidence.",
      activeDescription:
        "Start with the highest-scoring papers — they're the most relevant to your concept",
      activeHints: [
        { label: "Click a title to see details, abstract, and scoring rationale" },
        { label: "Download PDFs to read the full text inline" },
        { label: "Highlight key passages as evidence for your argument" },
        { label: "Add notes, tags, and set your reading status" },
        { label: "Dismiss papers that aren't relevant to narrow your corpus" },
      ],
    },
    precondition: (d) => d.scoredCount > 0,
    isComplete: (d) => d.readOrSkimmedCount > 0,
    relatedWalkthrough: "building-your-argument",
  },
  {
    id: "analyze",
    type: "core",
    icon: Telescope,
    content: {
      label: "Analyze the field",
      description:
        "Step back from individual papers and look at the literature as a whole — debate map, coverage gaps, and where your argument sits.",
      activeDescription:
        "Now zoom out — see the camps, the tensions, and where the gaps are. This is where your angle becomes clear.",
      activeHints: [
        { label: "Open the Field Debate map to see the intellectual camps" },
        { label: "Run a coverage audit to find gaps in your corpus" },
        { label: "Use Position Analysis to locate your argument in the debate" },
      ],
    },
    precondition: (d) => d.readOrSkimmedCount >= 1,
    isComplete: (d) => d.hasDebateMap || d.hasUsedPositionAnalysis,
    relatedWalkthrough: "leveraging-the-analyze-page",
  },
  {
    id: "build",
    type: "core",
    icon: Map,
    content: {
      label: "Build evidence map",
      description: "Add claims and link evidence from papers to construct your argument map.",
      activeDescription:
        "The evidence map turns reading into structured argument — claims, evidence, and the connections between them",
      activeHints: [
        { label: "Open the Evidence Map from the toolbar" },
        { label: "Create a claim — a small, falsifiable proposition" },
        { label: "Attach evidence from papers (supports / challenges / qualifies)" },
      ],
    },
    precondition: (d) => d.readOrSkimmedCount > 0,
    isComplete: (d) => d.claimCount > 0,
    relatedWalkthrough: "planting-the-flag",
  },
];

// ---------------------------------------------------------------------------
// Features (13) — formerly MILESTONES
// ---------------------------------------------------------------------------

export const FEATURES: FeatureEntry[] = [
  {
    id: "highlights",
    type: "feature",
    parentStep: "review",
    color: "peach",
    icon: Highlighter,
    content: FEATURE_CONTENT["highlights"],
    appearThreshold: (d) => d.readOrSkimmedCount >= 1,
    precondition: (d) => d.pdfOpenCount > 0,
    isComplete: (d) => d.hasHighlights,
    priority: 3,
    relatedWalkthrough: "building-your-argument",
    targetRef: "highlightsButton",
  },
  {
    id: "smart-trawl",
    type: "feature",
    parentStep: "trawl",
    color: "purple",
    icon: Sparkles,
    content: FEATURE_CONTENT["smart-trawl"],
    appearThreshold: (d) => d.readOrSkimmedCount >= 1 && d.hasHighlights,
    precondition: (d) => d.hasHighlights,
    isComplete: (d) => d.hasUsedSmartTrawl,
    priority: 2,
    relatedWalkthrough: "discovery-via-citation-networks",
    targetRef: "smartTrawlButton",
  },
  {
    id: "working-sets",
    type: "feature",
    parentStep: "review",
    color: "green",
    icon: FolderOpen,
    content: FEATURE_CONTENT["working-sets"],
    appearThreshold: (d) => d.totalPapers >= 10,
    precondition: (d) => d.totalPapers >= 30,
    isComplete: (d) => d.hasWorkingSets,
    priority: 4,
    relatedWalkthrough: "corpus-curation",
    targetRef: "workingSetsButton",
  },
  {
    id: "field-debate",
    type: "feature",
    parentStep: "analyze",
    color: "blue",
    icon: MessageSquare,
    content: FEATURE_CONTENT["field-debate"],
    appearThreshold: (d) => d.scoredCount >= 10 && d.readOrSkimmedCount >= 1,
    precondition: (d) => d.scoredCount >= 15 && d.readOrSkimmedCount >= 2,
    isComplete: (d) => d.hasDebateMap,
    priority: 1,
    relatedWalkthrough: "leveraging-the-analyze-page",
    targetRef: "fieldDebateButton",
  },
  {
    id: "position-analysis",
    type: "feature",
    parentStep: "analyze",
    color: "purple",
    icon: Compass,
    content: FEATURE_CONTENT["position-analysis"],
    appearThreshold: (d) => d.hasDebateMap,
    precondition: (d) => d.hasDebateMap && d.hasClaimsWithEvidence,
    isComplete: (d) => d.hasUsedPositionAnalysis,
    priority: 5,
    relatedWalkthrough: "planting-the-flag",
    targetRef: "positionAnalysisButton",
  },
  {
    id: "monitor-digest",
    type: "feature",
    parentStep: "review",
    color: "blue",
    icon: Mail,
    content: FEATURE_CONTENT["monitor-digest"],
    appearThreshold: (d) => daysSince(d.conceptCreatedAt) >= 3,
    precondition: (d) => d.monitorEnabled,
    isComplete: (d) => !!d.digestFrequency && d.digestFrequency !== "off",
    priority: 6,
    relatedWalkthrough: "paperchase-learns-from-you",
    targetRef: "livingLitButton",
  },
  {
    id: "calibrate-scoring",
    type: "feature",
    parentStep: "score",
    color: "purple",
    icon: Sliders,
    content: FEATURE_CONTENT["calibrate-scoring"],
    appearThreshold: (d) => d.scoredCount >= 5,
    precondition: (d) => d.scoredCount >= 5,
    isComplete: (d) => d.hasOverriddenScore,
    priority: 1,
    relatedWalkthrough: "paperchase-learns-from-you",
    targetRef: "scoreOverrideControl",
  },
  {
    id: "refine-concept",
    type: "feature",
    parentStep: "score",
    color: "purple",
    icon: Brain,
    content: FEATURE_CONTENT["refine-concept"],
    appearThreshold: (d) => d.scoredCount >= 10,
    precondition: (d) => d.scoredCount >= 10,
    isComplete: (d) => d.hasRefinedConcept && d.hasRescoredAfterRefine,
    priority: 2,
    targetRef: "refineConceptButton",
    relatedWalkthrough: "conceptual-frameworks",
  },
  // ===== NEW 7 FEATURES =====
  {
    id: "dismiss-similar",
    type: "feature",
    parentStep: "trawl",
    color: "blue",
    icon: Filter,
    content: FEATURE_CONTENT["dismiss-similar"],
    appearThreshold: (d) => d.scoredCount >= 5,
    precondition: (d) => d.scoredCount >= 5,
    isComplete: (d) => d.hasUsedDismissSimilar,
    priority: 7,
    relatedWalkthrough: "corpus-curation",
    targetRef: "dismissSimilarButton",
  },
  {
    id: "tags-notes",
    type: "feature",
    parentStep: "review",
    color: "peach",
    icon: Tag,
    content: FEATURE_CONTENT["tags-notes"],
    appearThreshold: (d) => d.readOrSkimmedCount >= 1,
    precondition: (d) => d.readOrSkimmedCount >= 1,
    isComplete: (d) => d.hasTaggedOrNotedPaper,
    priority: 9,
    relatedWalkthrough: "corpus-curation",
    targetRef: "paperDetailTagsSection",
  },
  {
    id: "dynamic-chat",
    type: "feature",
    parentStep: "analyze",
    color: "purple",
    icon: MessageCircle,
    content: FEATURE_CONTENT["dynamic-chat"],
    appearThreshold: (d) => d.pdfOpenCount >= 1,
    precondition: (d) => d.pdfOpenCount >= 1,
    isComplete: (d) => d.hasUsedDynamicChat,
    priority: 10,
    relatedWalkthrough: "paperchase-learns-from-you",
    targetRef: "dynamicChatPanel",
  },
  {
    id: "coverage-audit",
    type: "feature",
    parentStep: "analyze",
    color: "blue",
    icon: Scale,
    content: FEATURE_CONTENT["coverage-audit"],
    appearThreshold: (d) => d.scoredCount >= 15,
    precondition: (d) => d.scoredCount >= 20,
    isComplete: (d) => d.hasRunCoverageAudit,
    priority: 11,
    relatedWalkthrough: "leveraging-the-analyze-page",
    targetRef: "coverageAuditButton",
  },
  {
    id: "literature-landscape",
    type: "feature",
    parentStep: "analyze",
    color: "blue",
    icon: Globe2,
    content: FEATURE_CONTENT["literature-landscape"],
    appearThreshold: (d) => d.hasDebateMap,
    precondition: (d) => d.hasDebateMap && d.scoredCount >= 15,
    isComplete: (d) => d.hasViewedLandscape,
    priority: 8,
    relatedWalkthrough: "leveraging-the-analyze-page",
    targetRef: "landscapeButton",
  },
  {
    id: "writing-workspace",
    type: "feature",
    parentStep: "build",
    color: "purple",
    icon: PenSquare,
    content: FEATURE_CONTENT["writing-workspace"],
    appearThreshold: (d) => d.claimCount >= 1,
    precondition: (d) => d.claimCount >= 3,
    isComplete: (d) => d.hasOpenedWritingWorkspace,
    priority: 12,
    relatedWalkthrough: "using-the-output",
    targetRef: "writingWorkspaceButton",
  },
  {
    id: "monitor-triage",
    type: "feature",
    parentStep: "review",
    color: "blue",
    icon: Workflow,
    content: FEATURE_CONTENT["monitor-triage"],
    // Monitor is on by default for every new concept (Sprint 11A), so
    // `monitorEnabled` alone fires on day-0. The feature is only meaningful
    // once there are enough papers for a digest to surface candidates; gate
    // on totalPapers so the pill stages with the rest of the corpus.
    appearThreshold: (d) => d.monitorEnabled && d.totalPapers >= 30,
    precondition: (d) => d.monitorEnabled,
    isComplete: (d) => d.hasUsedMonitorTriage,
    priority: 13,
    relatedWalkthrough: "paperchase-learns-from-you",
    targetRef: "livingLitButton",
  },
];

// ---------------------------------------------------------------------------
// Walkthroughs (10)
// ---------------------------------------------------------------------------

export const WALKTHROUGHS: WalkthroughEntry[] = [
  {
    id: "lifecycle-of-an-argument",
    type: "walkthrough",
    group: "start-here",
    icon: Sparkles,
    content: WALKTHROUGH_CONTENT["lifecycle-of-an-argument"],
    relatedWalkthroughs: [
      "conceptual-frameworks",
      "discovery-via-citation-networks",
      "building-your-argument",
    ],
  },
  {
    id: "conceptual-frameworks",
    type: "walkthrough",
    group: "foundations",
    icon: Brain,
    content: WALKTHROUGH_CONTENT["conceptual-frameworks"],
    relatedWalkthroughs: ["lifecycle-of-an-argument", "discovery-via-citation-networks"],
  },
  {
    id: "paperchase-learns-from-you",
    type: "walkthrough",
    group: "foundations",
    icon: Sparkles,
    content: WALKTHROUGH_CONTENT["paperchase-learns-from-you"],
    relatedWalkthroughs: ["corpus-curation"],
  },
  {
    id: "discovery-via-citation-networks",
    type: "walkthrough",
    group: "corpus",
    icon: Network,
    content: WALKTHROUGH_CONTENT["discovery-via-citation-networks"],
    relatedWalkthroughs: ["importing-existing-bibliographies", "corpus-curation"],
  },
  {
    id: "importing-existing-bibliographies",
    type: "walkthrough",
    group: "corpus",
    icon: FileUp,
    content: WALKTHROUGH_CONTENT["importing-existing-bibliographies"],
    relatedWalkthroughs: ["discovery-via-citation-networks", "corpus-curation"],
  },
  {
    id: "corpus-curation",
    type: "walkthrough",
    group: "corpus",
    icon: Filter,
    content: WALKTHROUGH_CONTENT["corpus-curation"],
    relatedWalkthroughs: ["discovery-via-citation-networks", "paperchase-learns-from-you"],
  },
  {
    id: "building-your-argument",
    type: "walkthrough",
    group: "analysis",
    icon: Map,
    content: WALKTHROUGH_CONTENT["building-your-argument"],
    relatedWalkthroughs: ["leveraging-the-analyze-page", "planting-the-flag"],
  },
  {
    id: "leveraging-the-analyze-page",
    type: "walkthrough",
    group: "analysis",
    icon: BarChart3,
    content: WALKTHROUGH_CONTENT["leveraging-the-analyze-page"],
    relatedWalkthroughs: ["building-your-argument", "planting-the-flag"],
  },
  {
    id: "planting-the-flag",
    type: "walkthrough",
    group: "analysis",
    icon: Flag,
    content: WALKTHROUGH_CONTENT["planting-the-flag"],
    relatedWalkthroughs: ["building-your-argument", "using-the-output"],
  },
  {
    id: "using-the-output",
    type: "walkthrough",
    group: "output",
    icon: Download,
    content: WALKTHROUGH_CONTENT["using-the-output"],
    relatedWalkthroughs: ["building-your-argument"],
  },
];

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

export function getFeature(id: string): FeatureEntry | undefined {
  return FEATURES.find((f) => f.id === id);
}

export function getWalkthrough(id: string): WalkthroughEntry | undefined {
  return WALKTHROUGHS.find((w) => w.id === id);
}

/**
 * Resolve any guide id — a walkthrough id, a Feature id, or a CoreStep id — to a
 * live walkthrough id. Feature/CoreStep ids map through their `relatedWalkthrough`
 * (the registry's curated "read more here" pointer). Returns the input unchanged
 * if nothing matches, so the caller still sees (and can handle) the miss.
 *
 * This exists because deep-links (e.g. the Workflow Advisor) may reference
 * features or core steps by id, but the walkthrough overlay can only render the
 * 10 MDX walkthroughs. Without this mapping those links open a blank modal.
 */
export function resolveWalkthroughId(id: string): string {
  if (WALKTHROUGHS.some((w) => w.id === id)) return id;
  const feature = FEATURES.find((f) => f.id === id);
  if (feature?.relatedWalkthrough) return feature.relatedWalkthrough;
  const core = CORE_STEPS.find((c) => c.id === id);
  if (core?.relatedWalkthrough) return core.relatedWalkthrough;
  return id;
}

const WALKTHROUGH_GROUP_ORDER: WalkthroughGroup[] = [
  "start-here",
  "foundations",
  "corpus",
  "analysis",
  "output",
];

export function walkthroughsByGroup(): Record<WalkthroughGroup, WalkthroughEntry[]> {
  const byGroup: Record<WalkthroughGroup, WalkthroughEntry[]> = {
    "start-here": [],
    foundations: [],
    corpus: [],
    analysis: [],
    output: [],
  };
  for (const w of WALKTHROUGHS) byGroup[w.group].push(w);
  return byGroup;
}

export function featuresByParent(): Record<string, FeatureEntry[]> {
  const byParent: Record<string, FeatureEntry[]> = {};
  for (const f of FEATURES) {
    if (f.parentStep === "dashboard") continue; // dashboard-only
    if (!byParent[f.parentStep]) byParent[f.parentStep] = [];
    byParent[f.parentStep].push(f);
  }
  // Sort each parent's features by priority (lowest number = highest priority)
  for (const parent of Object.keys(byParent)) {
    byParent[parent].sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
  }
  return byParent;
}

export { WALKTHROUGH_GROUP_ORDER };
